import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import {
  DepositRequest, LoadDefaultsResult, PaymentWithholding, RegisterFilter, SupplierWithholdingDefaults,
  WithholdingDeposit, WithholdingProposal, WithholdingRule, WithholdingRuleRequest, WithholdingSettings,
  WithholdingSettingsRequest, WithholdingType, UndepositedWithholdingSummary,
} from '../interfaces/procurement/withholding.interface';

const cleanParams = (f: RegisterFilter) => {
  const p: Record<string, string | boolean> = {};
  if (f.from) p.from = f.from;
  if (f.to) p.to = f.to;
  if (f.type) p.type = f.type;
  if (f.supplierId) p.supplierId = f.supplierId;
  if (f.includeVoided) p.includeVoided = true;
  return p;
};

export const WithholdingService = {
  getSettings: async (institutionId: string): Promise<WithholdingSettings> =>
    (await axios.get(ENDPOINTS.WITHHOLDING.SETTINGS(institutionId))).data,
  saveSettings: async (institutionId: string, dto: WithholdingSettingsRequest): Promise<WithholdingSettings> =>
    (await axios.put(ENDPOINTS.WITHHOLDING.SETTINGS(institutionId), dto)).data,

  listRules: async (institutionId: string): Promise<WithholdingRule[]> =>
    (await axios.get(ENDPOINTS.WITHHOLDING.RULES(institutionId))).data,
  createRule: async (institutionId: string, dto: WithholdingRuleRequest): Promise<WithholdingRule> =>
    (await axios.post(ENDPOINTS.WITHHOLDING.RULES(institutionId), dto)).data,
  updateRule: async (institutionId: string, id: string, dto: WithholdingRuleRequest): Promise<WithholdingRule> =>
    (await axios.put(ENDPOINTS.WITHHOLDING.RULE(institutionId, id), dto)).data,
  setRuleActive: async (institutionId: string, id: string, active: boolean): Promise<WithholdingRule> =>
    (await axios.post(ENDPOINTS.WITHHOLDING.RULE_ACTIVE(institutionId, id, active))).data,
  loadDefaults: async (institutionId: string): Promise<LoadDefaultsResult> =>
    (await axios.post(ENDPOINTS.WITHHOLDING.LOAD_DEFAULTS(institutionId))).data,

  saveSupplierDefaults: async (supplierId: string, dto: SupplierWithholdingDefaults): Promise<void> => {
    await axios.put(ENDPOINTS.WITHHOLDING.SUPPLIER_DEFAULTS(supplierId), dto);
  },
  /** With `ruleIds`, proposes exactly those rules (a line whose rule was switched) instead of the supplier defaults. */
  getProposal: async (billId: string, amount: number, ruleIds?: string[]): Promise<WithholdingProposal> =>
    (await axios.get(ENDPOINTS.WITHHOLDING.PROPOSAL, {
      params: { billId, amount, ...(ruleIds && ruleIds.length ? { ruleIds: ruleIds.join(',') } : {}) },
    })).data,
  listForPayment: async (paymentId: string): Promise<PaymentWithholding[]> =>
    (await axios.get(ENDPOINTS.WITHHOLDING.FOR_PAYMENT(paymentId))).data,
  downloadCertificate: async (id: string): Promise<Blob> =>
    (await axios.get(ENDPOINTS.WITHHOLDING.CERTIFICATE_PDF(id), { responseType: 'blob' })).data,

  listDeposits: async (institutionId: string): Promise<WithholdingDeposit[]> =>
    (await axios.get(ENDPOINTS.WITHHOLDING.DEPOSITS(institutionId))).data,
  listUndeposited: async (institutionId: string, type: WithholdingType, from?: string, to?: string):
    Promise<PaymentWithholding[]> =>
    (await axios.get(ENDPOINTS.WITHHOLDING.UNDEPOSITED(institutionId), { params: { type, from, to } })).data,
  getUndepositedSummary: async (institutionId: string): Promise<UndepositedWithholdingSummary> =>
    (await axios.get(ENDPOINTS.WITHHOLDING.UNDEPOSITED_SUMMARY(institutionId))).data,
  createDeposit: async (institutionId: string, dto: DepositRequest): Promise<WithholdingDeposit> =>
    (await axios.post(ENDPOINTS.WITHHOLDING.DEPOSITS(institutionId), dto)).data,
  reverseDeposit: async (id: string): Promise<WithholdingDeposit> =>
    (await axios.post(ENDPOINTS.WITHHOLDING.DEPOSIT_REVERSE(id))).data,

  register: async (institutionId: string, f: RegisterFilter): Promise<PaymentWithholding[]> =>
    (await axios.get(ENDPOINTS.WITHHOLDING.REGISTER(institutionId), { params: cleanParams(f) })).data,
  exportRegister: async (institutionId: string, format: 'csv' | 'xlsx', f: RegisterFilter): Promise<Blob> =>
    (await axios.get(ENDPOINTS.WITHHOLDING.REGISTER_EXPORT(institutionId),
      { params: { ...cleanParams(f), format }, responseType: 'blob' })).data,
};
